import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CancelButton, ForwardButton } from "../Primitives/Buttons";
import { CsvExportIcon, SaveIcon } from "../Primitives/Icons";
import { IPomReportingExcelImportDataModel, IPomReportingExcelImportPositionModel } from "../../repositories/models/reportings/pom-reporting-excel-import.model";
import { PomReportingStatus } from "../../repositories/models/reportings/pom-reporting-status";
import { useImportReportingMutation } from "../../repositories/queries/mutations/reportings/import-reporting.query";
import { useDownloadReportingImportTemplateMutation } from "../../repositories/queries/reportings/download-reporting-import-template.query";

type DialogStep = "idle" | "uploading" | "results";

interface IPomReportingExcelImportDialogProps {
  open: boolean;
  contractNo: string;
  reportingNo: number;
  currentStatus: PomReportingStatus | undefined;
  onApply: (positions: IPomReportingExcelImportPositionModel[]) => void;
  onClose: () => void;
}

export const PomReportingExcelImportDialog: FC<IPomReportingExcelImportDialogProps> = (props) => {
  const { open, contractNo, reportingNo, currentStatus, onApply, onClose } = props;
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const languageLabel: Record<"de" | "en", string> = {
    de: "DE",
    en: "EN",
  };

  const [step, setStep] = useState<DialogStep>("idle");
  const [importResult, setImportResult] = useState<IPomReportingExcelImportDataModel | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedLang, setSelectedLang] = useState<"de" | "en">("de");

  const { mutateAsync: downloadTemplate, isPending: isDownloading } = useDownloadReportingImportTemplateMutation();
  const { mutateAsync: importFile } = useImportReportingMutation();

  const isAlreadyReported =
    currentStatus === PomReportingStatus.Reported || currentStatus === PomReportingStatus.Corrected;

  const handleClose = (): void => {
    setStep("idle");
    setImportResult(null);
    setUploadError(null);
    onClose();
  };

  const handleDownloadTemplate = (): void => {
    void downloadTemplate({ contractNo, reportingNo, lang: selectedLang });
  };

  const handleSelectLang = (lang: "de" | "en"): void => {
    setSelectedLang(lang);
    setLangMenuAnchor(null);
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be re-uploaded
    event.target.value = "";

    setStep("uploading");
    setUploadError(null);
    setImportResult(null);

    try {
      const result = await importFile({ contractNo, reportingNo, file });
      setImportResult(result);
      setStep("results");
    } catch {
      setUploadError(t("pom.reportings.import_dialog.upload_error"));
      setStep("idle");
    }
  };

  const handleApply = (): void => {
    if (!importResult) return;
    onApply(importResult.positions);
    handleClose();
  };

  const canApply = importResult !== null && importResult.positions.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("pom.reportings.import_dialog.title")}</DialogTitle>
      <DialogContent>
        {/* Correction hint */}
        {isAlreadyReported && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("pom.reportings.import_dialog.correction_hint")}
          </Alert>
        )}

        {/* Step: idle */}
        {step === "idle" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography>{t("pom.reportings.import_dialog.instruction")}</Typography>
            {uploadError && <Alert severity="error">{uploadError}</Alert>}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "center" }}>
              <ButtonGroup variant="outlined" disabled={isDownloading}>
                <Button onClick={handleDownloadTemplate} endIcon={<SaveIcon svgColor="green" />}>
                  {t("pom.reportings.import_dialog.download_template")}
                </Button>
                <Button size="small" aria-label="select template language" onClick={(e) => setLangMenuAnchor(e.currentTarget)}>
                  {languageLabel[selectedLang]}
                </Button>
              </ButtonGroup>
              <Menu anchorEl={langMenuAnchor} open={Boolean(langMenuAnchor)} onClose={() => setLangMenuAnchor(null)}>
                <MenuItem selected={selectedLang === "de"} onClick={() => handleSelectLang("de")}>DE - Deutsch</MenuItem>
                <MenuItem selected={selectedLang === "en"} onClick={() => handleSelectLang("en")}>EN - English</MenuItem>
              </Menu>
              <Button
                variant="contained"
                color="primary"
                onClick={() => fileInputRef.current?.click()}
                endIcon={<CsvExportIcon />}
              >
                {t("pom.reportings.import_dialog.select_file")}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step: uploading */}
        {step === "uploading" && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Step: results */}
        {step === "results" && importResult && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity={importResult.positions.length > 0 ? "success" : "warning"}>
              {t("pom.reportings.import_dialog.positions_found", { count: importResult.positions.length })}
            </Alert>
            {importResult.invalidRows.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("pom.reportings.import_dialog.invalid_rows_hint", { count: importResult.invalidRows.length })}
                </Typography>
                <List dense disablePadding>
                  {importResult.invalidRows.map((row) => (
                    <ListItem key={row.positionNo} disablePadding>
                      <Typography variant="body2">
                        {t("pom.reportings.import_dialog.invalid_row_item", {
                          positionNo: row.positionNo,
                          rawValue: row.rawValue,
                        })}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
            <Button variant="contained" color="primary" onClick={() => fileInputRef.current?.click()} endIcon={<CsvExportIcon />}>
              {t("pom.reportings.import_dialog.select_different_file")}
            </Button>
          </Box>
        )}

        {/* Hidden File-Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={(e) => void handleFileInputChange(e)}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <CancelButton onClick={handleClose}>
          <Typography variant="body1">{t("pom.reportings.import_dialog.cancel")}</Typography>
        </CancelButton>
        <ForwardButton onClick={handleApply} disabled={!canApply}>
          <Typography variant="body1">{t("pom.reportings.import_dialog.apply")}</Typography>
        </ForwardButton>
      </DialogActions>
    </Dialog>
  );
};
